import { useEffect, useState } from 'react';
import type { ProductListItem, StockTransferRow, StockTransferStatus, WarehouseRow } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatDate, formatQty } from '../format';
import { PageHeader } from '../components/ListPage';

// Anbarlar arası yerdəyişmə (KT-93): «Göndər» → mal «yolda»; hədəf anbarda «Qəbul et» (qismən ola bilər);
// «Qəbul bitdi» — gəlməyən miqdar yolda itki kimi silinir, fərq (zədəli, itki) sətirdə qalır. Qəbul olunmamış — «Ləğv et».
// Göndərmək (stock.transfer) və qəbul etmək (stock.receive) ayrı icazələrdir.

const STATUS: Record<StockTransferStatus, string> = { in_transit: 'Yolda', partial: 'Qismən qəbul', received: 'Qəbul olunub', cancelled: 'Ləğv olunub' };
const FILTERS: [string, string][] = [['', 'Hamısı'], ['in_transit', 'Yolda'], ['partial', 'Qismən qəbul'], ['received', 'Qəbul olunub'], ['cancelled', 'Ləğv olunub']];

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.';
}

interface Draft {
  from: string;
  to: string;
  note: string;
  lines: { productId: string; name: string; qty: string }[];
}

export function StockTransfers({ canSend, canReceive }: { canSend: boolean; canReceive: boolean }) {
  const [rows, setRows] = useState<StockTransferRow[] | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseRow[]>([]);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<ProductListItem[]>([]);
  const [receive, setReceive] = useState<{ row: StockTransferRow; qty: Record<string, string>; damaged: Record<string, string>; final: boolean } | null>(null);
  // İdempotentlik açarı hər əməliyyat üçün ayrı (göndər, qəbul:<id>, ləğv:<id>): uğurda silinir
  const [attempts, setAttempts] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = (f = filter) =>
    api
      .stockTransfers(f || undefined)
      .then(setRows)
      .catch((err) => setMsg({ ok: false, text: errText(err) }));
  useEffect(() => {
    api.warehouses().then((w) => setWarehouses(w.filter((x) => x.active))).catch(() => setWarehouses([]));
  }, []);
  useEffect(() => {
    void load(filter);
  }, [filter]);
  useEffect(() => {
    if (search.trim().length < 2) return setFound([]);
    const t = setTimeout(() => {
      api.products({ q: search.trim(), limit: 10 }).then((r) => setFound(r.items)).catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  // İdempotentlik: eyni cəhd təkrar göndərilsə ikinci sənəd yaranmır (attempt id uğurdan sonra sıfırlanır)
  async function run(key: string, fn: (id: string) => Promise<string>) {
    const id = attempts[key] ?? crypto.randomUUID();
    setAttempts((a) => ({ ...a, [key]: id }));
    setBusy(true);
    setMsg(null);
    try {
      setMsg({ ok: true, text: await fn(id) });
      setAttempts((a) => {
        const next = { ...a };
        delete next[key];
        return next;
      });
      setDraft(null);
      setReceive(null);
      await load();
    } catch (err) {
      setMsg({ ok: false, text: errText(err) });
    } finally {
      setBusy(false);
    }
  }

  function send() {
    if (!draft) return;
    const lines = draft.lines.map((l) => ({ productId: l.productId, qty: Number(l.qty.replace(',', '.')) })).filter((l) => l.qty > 0);
    if (!draft.from || !draft.to || lines.length === 0) return setMsg({ ok: false, text: 'Anbarları və ən azı bir malı seçin' });
    void run('send', async (id) => {
      const r = await api.stockTransferSend({ id, fromWarehouseId: draft.from, toWarehouseId: draft.to, note: draft.note || undefined, lines });
      return `${r.displayNo} göndərildi — mal yoldadır.`;
    });
  }

  function doReceive() {
    if (!receive) return;
    const lines = receive.row.lines.map((l) => ({
      lineId: l.lineId,
      qty: Number((receive.qty[l.lineId] ?? '0').replace(',', '.')) || 0,
      damaged: Number((receive.damaged[l.lineId] ?? '0').replace(',', '.')) || 0,
    }));
    const losing = receive.final && receive.row.lines.some((l) => {
      const left = Math.round((l.sent - l.received - l.lost) * 1000) / 1000;
      const got = Number((receive.qty[l.lineId] ?? '0').replace(',', '.')) || 0;
      return got < left;
    });
    if (losing && !window.confirm('Gəlməyən miqdar yolda itki kimi yazılacaq və yerdəyişmə bağlanacaq. Davam edək?')) return;
    void run(`receive:${receive.row.id}`, async (id) => {
      const r = await api.stockTransferReceive(receive.row.id, { id, final: receive.final, lines });
      return receive.final ? `${r.displayNo}: qəbul bitdi.` : `${r.displayNo}: qismən qəbul yazıldı.`;
    });
  }

  const name = (id: string) => warehouses.find((w) => w.id === id)?.name ?? '';
  // Sətir: eyni mal ikinci dəfə əlavə olunmur (miqdarı dəyişdirilir)
  const addLine = (p: ProductListItem) => {
    setDraft((d) => (d && !d.lines.some((l) => l.productId === p.id) ? { ...d, lines: [...d.lines, { productId: p.id, name: p.name, qty: String(p.matchedQty ?? 1) }] } : d));
    setSearch('');
    setFound([]);
  };

  return (
    <main className="page page-list">
      <PageHeader
        title="Yerdəyişmə"
        sub="Anbarlar arası"
        actions={
          canSend &&
          !draft && (
            <button type="button" className="btn-primary btn-small" onClick={() => setDraft({ from: warehouses.find((w) => w.isMain)?.id ?? '', to: '', note: '', lines: [] })}>
              + Yeni yerdəyişmə
            </button>
          )
        }
      />
      {msg && <p className={msg.ok ? 'ok' : 'bad'} role="status">{msg.text}</p>}

      {draft && (
        <section className="doc-form doc-inline sec-anbar" aria-label="Yeni yerdəyişmə">
          <header className="doc-head">
            <div>
              <span className="doc-eyebrow">MAL VƏ ANBAR · YERDƏYİŞMƏ</span>
              <h2>
                Yeni yerdəyişmə <span className="badge badge-off">Qaralama</span>
              </h2>
              <span className="muted small">Nömrə göndəriləndə verilir · mal göndəriləndə «yolda» olur</span>
            </div>
          </header>

          <section className="card doc-section" aria-label="Sənəd məlumatları">
            <h3>Sənəd məlumatları</h3>
            <div className="doc-grid">
              <label className="field">
                <span>Haradan</span>
                <select aria-label="Haradan" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} disabled={busy}>
                  <option value="">Seçin</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{w.outletName} · {w.name}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Haraya</span>
                <select aria-label="Haraya" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} disabled={busy}>
                  <option value="">Seçin</option>
                  {warehouses.filter((w) => w.id !== draft.from).map((w) => (
                    <option key={w.id} value={w.id}>{w.outletName} · {w.name}</option>
                  ))}
                </select>
              </label>
              <label className="field doc-wide">
                <span>Qeyd</span>
                <input aria-label="Qeyd" value={draft.note} maxLength={300} placeholder="Əlavə məlumat…" onChange={(e) => setDraft({ ...draft, note: e.target.value })} disabled={busy} />
              </label>
            </div>
            {draft.from && draft.to && <p className="doc-info">«{name(draft.from)}» → yolda → «{name(draft.to)}». Hədəf anbar qəbul edəndə mal orada görünəcək.</p>}
          </section>

          <section className="card doc-section" aria-label="Mallar">
            <h3>Mallar · {draft.lines.length} sətir</h3>
            <div className="table-wrap">
              <table className="data-table doc-lines" aria-label="Yerdəyişmə malları">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">Mal</th>
                    <th scope="col" className="num">Miqdar</th>
                    <th scope="col"><span className="sr-only">Sil</span></th>
                  </tr>
                </thead>
                <tbody>
                  {draft.lines.map((l, i) => (
                    <tr key={l.productId}>
                      <td className="muted">{i + 1}</td>
                      <td><span className="product-name">{l.name}</span></td>
                      <td className="num">
                        <input inputMode="decimal" aria-label={`${l.name} — miqdar`} value={l.qty} onChange={(e) => setDraft({ ...draft, lines: draft.lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)) })} disabled={busy} />
                      </td>
                      <td>
                        <button type="button" className="doc-del" aria-label={`${l.name} sətrini sil`} onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, j) => j !== i) })} disabled={busy}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="doc-add">
                    <td className="muted">{draft.lines.length + 1}</td>
                    <td colSpan={3}>
                      <input
                        type="search"
                        aria-label="Məhsul axtarışı"
                        value={search}
                        placeholder="Mal əlavə et: ad və ya barkod yaz, Enter"
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' || e.ctrlKey || e.metaKey) return;
                          e.preventDefault();
                          // Dəqiq barkod və ya tək nəticə — dərhal sətir; yoxsa siyahıdan seçilir
                          const q = search.trim();
                          const hit = found.find((p) => p.barcodes.includes(q)) ?? (found.length === 1 ? found[0] : null);
                          if (hit) addLine(hit);
                        }}
                        disabled={busy}
                      />
                      {found.length > 0 && (
                        <ul className="pick-list" aria-live="polite" aria-label="Tapılan mallar">
                          {found.map((p) => (
                            <li key={p.id}>
                              <button type="button" className="link-btn" onClick={() => addLine(p)}>
                                {p.name}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <footer className="doc-bar">
            <span className="muted small doc-bar-hint">Enter — mal əlavə et · göndəriləndən sonra hədəf anbar qəbul edir</span>
            <button type="button" className="btn-secondary doc-cancel" onClick={() => setDraft(null)} disabled={busy}>
              Ləğv et
            </button>
            <button type="button" className="btn-primary" onClick={send} disabled={busy || !draft.from || !draft.to || draft.lines.length === 0}>
              Göndər
            </button>
          </footer>
        </section>
      )}

      {receive && (
        <section className="card form-stack" aria-label="Qəbul">
          <h2>Qəbul — {receive.row.displayNo}</h2>
          {receive.row.lines.map((l) => {
            const left = Math.round((l.sent - l.received - l.lost) * 1000) / 1000;
            return (
              <div key={l.lineId} className="purchase-line">
                <span className="product-name">
                  {l.name} <span className="muted small">— göndərilib {formatQty(l.sent)}, qəbul {formatQty(l.received)}, gözlənir {formatQty(left)}</span>
                </span>
                <label>
                  Gəldi
                  <input inputMode="decimal" aria-label={`${l.name} — gəldi`} value={receive.qty[l.lineId] ?? ''} onChange={(e) => setReceive({ ...receive, qty: { ...receive.qty, [l.lineId]: e.target.value } })} disabled={busy} />
                </label>
                <label>
                  Zədəli
                  <input inputMode="decimal" aria-label={`${l.name} — zədəli`} value={receive.damaged[l.lineId] ?? ''} onChange={(e) => setReceive({ ...receive, damaged: { ...receive.damaged, [l.lineId]: e.target.value } })} disabled={busy} />
                </label>
              </div>
            );
          })}
          <label className="check">
            <input type="checkbox" checked={receive.final} onChange={(e) => setReceive({ ...receive, final: e.target.checked })} disabled={busy} /> Qəbul bitdi — gəlməyən miqdar yolda itki kimi yazılsın
          </label>
          <div className="form-actions">
            <button type="button" className="btn-primary btn-small" onClick={doReceive} disabled={busy}>
              Qəbul et
            </button>
            <button type="button" className="btn-small" onClick={() => setReceive(null)} disabled={busy}>
              Bağla
            </button>
          </div>
        </section>
      )}

      <div className="list-bar">
        <label>
          <span className="sr-only">Status</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Status">
            {FILTERS.map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </label>
      </div>
      {rows && rows.length === 0 && <p className="muted">Yerdəyişmə yoxdur.</p>}
      {rows?.map((r) => (
        <section key={r.id} className="card" aria-label={r.displayNo}>
          <div className="toolbar">
            <strong>{r.displayNo}</strong>
            <span className="muted small">{formatDate(r.postedAt)}</span>
            <span className={r.status === 'received' ? 'badge badge-ok' : r.status === 'cancelled' ? 'badge badge-off' : 'badge badge-bad'}>{STATUS[r.status]}</span>
            <span className="topbar-spacer" />
            {canReceive && (r.status === 'in_transit' || r.status === 'partial') && (
              <button
                type="button"
                className="btn-primary btn-small"
                disabled={busy}
                onClick={() =>
                  // «Gəldi» gözlənən miqdarla doldurulur; «Qəbul bitdi» susmaya görə seçilmir (səhv kliklə hamısı itki yazılmasın)
                  setReceive({
                    row: r,
                    qty: Object.fromEntries(r.lines.map((l) => [l.lineId, String(Math.round((l.sent - l.received - l.lost) * 1000) / 1000)])),
                    damaged: {},
                    final: false,
                  })
                }
              >
                Qəbul et
              </button>
            )}
            {canSend && r.status === 'in_transit' && (
              <button type="button" className="btn-small" disabled={busy} onClick={() => run(`cancel:${r.id}`, async (id) => `${(await api.stockTransferCancel(r.id, id)).displayNo}: ləğv olundu, mal geri qayıtdı.`)}>
                Ləğv et
              </button>
            )}
          </div>
          <p className="small">
            {r.fromWarehouse.name} → {r.toWarehouse.name}
            {r.note ? ` · ${r.note}` : ''}
          </p>
          <ul className="plain-list small">
            {r.lines.map((l) => (
              <li key={l.lineId}>
                {l.name}: göndərilib {formatQty(l.sent)}, qəbul {formatQty(l.received)}
                {l.damaged > 0 && <span className="bad"> · zədəli {formatQty(l.damaged)}</span>}
                {l.lost > 0 && <span className="bad"> · itki {formatQty(l.lost)}</span>}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </main>
  );
}
